import * as Schema from "effect/Schema";
import * as Rpc from "effect/unstable/rpc/Rpc";
import * as RpcGroup from "effect/unstable/rpc/RpcGroup";

import { EnvironmentAuthorizationError } from "./auth.ts";
import { Base64Pcm16Chunk, TrimmedNonEmptyString } from "./baseSchemas.ts";

/**
 * Dictation stream contract: speech-to-text into the composer draft.
 *
 * A dictation is a transient capture session — audio frames in, transcript
 * deltas out. It is not a thread, not a turn, and carries no thread identity:
 * the client owns where the transcript lands (the composer draft; nothing
 * writes a thread until the person sends). Audio bytes and transcripts are
 * pre-Atom working context — this contract deliberately carries no
 * provenance, snapshot, or retention fields.
 *
 * Wire encoding: PCM16 16 kHz mono frames ride as bounded base64 strings.
 * The `/ws` RPC transport is JSON-serialized end to end
 * (`RpcSerialization.layerJson` on server and every client), so raw binary
 * WebSocket frames are not admitted on this wire; bounded base64 keeps
 * dictation on the same transport as every other contract.
 *
 * Stream shape: `dictation.start` is a streaming RPC. Its first event is
 * `started` carrying the `dictationId` that `append`/`flush`/`stop` then
 * reference; transcript deltas and speech display events follow on the same
 * stream, which ends after `stopped`. Concurrent RPCs on one socket make the
 * append-while-streaming choreography ordinary.
 */
export const DICTATION_WS_METHODS = {
  dictationStart: "dictation.start",
  dictationAppend: "dictation.append",
  dictationFlush: "dictation.flush",
  dictationStop: "dictation.stop",
  dictationStatus: "dictation.status",
} as const;

export const DictationId = TrimmedNonEmptyString.pipe(Schema.brand("DictationId"));
export type DictationId = typeof DictationId.Type;

export const DictationSegmentId = TrimmedNonEmptyString.pipe(Schema.brand("DictationSegmentId"));
export type DictationSegmentId = typeof DictationSegmentId.Type;

/**
 * Which engine slot serves this dictation's speech-to-text. The default path
 * is local and requires no API key; `cloud` is an opt-in override for
 * configured providers.
 */
export const DictationSttSlot = Schema.Literals(["local", "cloud"]);
export type DictationSttSlot = typeof DictationSttSlot.Type;

/**
 * One audio frame: the shared bounded wire chunk (`Base64Pcm16Chunk`,
 * 49,152 PCM bytes ≈ 1.5 s at 16 kHz); clients typically send 100–250 ms
 * frames.
 */
export const DictationAudioFrame = Base64Pcm16Chunk;
export type DictationAudioFrame = typeof DictationAudioFrame.Type;

export const DictationStartInput = Schema.Struct({
  /** Explicit one-utterance capture; completion is never itself a Send. */
  utteranceMode: Schema.optional(Schema.Boolean),
  /** BCP-47 language hint for the STT engine. Omitted means engine default. */
  language: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(35))),
  /** Slot override for this dictation only. Omitted means configured default (local). */
  sttSlot: Schema.optional(DictationSttSlot),
  /**
   * Opaque references to vocabulary packs the STT pass should bias toward.
   * Their meaning (personal dictionary, derived context packs) belongs to the
   * dictionary capability; this contract only carries the refs.
   */
  dictionaryPacks: Schema.optional(
    Schema.Array(TrimmedNonEmptyString.check(Schema.isMaxLength(256))).check(
      Schema.isMaxLength(32),
    ),
  ),
  /** Explicit terms frozen for this local capture; never an engine/cloud hint. */
  vocabulary: Schema.optional(
    Schema.Array(
      Schema.Struct({
        term: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(128)),
        replace: Schema.optional(
          Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(128)),
        ),
      }),
    ).check(Schema.isMaxLength(256)),
  ),
});
export type DictationStartInput = Schema.Codec.Encoded<typeof DictationStartInput>;

const DictationSessionInput = Schema.Struct({
  dictationId: DictationId,
});
export type DictationSessionInput = Schema.Codec.Encoded<typeof DictationSessionInput>;

export const DictationAppendInput = Schema.Struct({
  ...DictationSessionInput.fields,
  audio: DictationAudioFrame,
});
export type DictationAppendInput = Schema.Codec.Encoded<typeof DictationAppendInput>;

export const DictationFlushInput = DictationSessionInput;
export type DictationFlushInput = Schema.Codec.Encoded<typeof DictationFlushInput>;

const CompletedDictationUtterance = Schema.Struct({
  id: TrimmedNonEmptyString,
  text: TrimmedNonEmptyString,
});

export const DictationStatus = Schema.Struct({
  /** One settled utterance, retained for owner readback until capture stops. */
  completedUtterance: Schema.optional(CompletedDictationUtterance),
  state: Schema.Literals(["open", "stopping"]),
  acceptedSamples: Schema.Number,
  bufferedSamples: Schema.Number,
  pendingSamples: Schema.Number,
  /** Native decode work may revisit already recognized acoustic context. */
  activeDecodes: Schema.optional(Schema.Number),
  retainedContextSamples: Schema.optional(Schema.Number),
  /** Latest segment only, owned by this capture and forgotten on close. */
  latestTranscript: Schema.optional(
    Schema.Struct({
      segmentId: DictationSegmentId,
      sourceText: Schema.String,
      text: Schema.String,
    }),
  ),
  /** A possible exact phrase prefix, still provisional and bounded by one entry. */
  pendingTranscript: Schema.optional(
    Schema.Struct({
      segmentId: DictationSegmentId,
      sourceText: Schema.String.check(Schema.isMaxLength(128)),
    }),
  ),
});
export type DictationStatus = typeof DictationStatus.Type;

export const DictationStopInput = DictationSessionInput;
export type DictationStopInput = Schema.Codec.Encoded<typeof DictationStopInput>;

/** First event on every dictation stream: the id `append`/`flush`/`stop` reference. */
const DictationStartedEvent = Schema.Struct({
  type: Schema.Literal("started"),
  dictationId: DictationId,
});

/**
 * A transcript delta. Non-final deltas replace the live segment's text;
 * a final delta commits the segment. Finals assemble in segment order.
 * `confidence` is `null` when the engine reports none (local offline
 * decoding does not score).
 */
const DictationTranscriptEvent = Schema.Struct({
  type: Schema.Literal("transcript"),
  segmentId: DictationSegmentId,
  text: Schema.String,
  isFinal: Schema.Boolean,
  confidence: Schema.NullOr(Schema.Number.check(Schema.isBetween({ minimum: 0, maximum: 1 }))),
});

/** Display-only voice-activity events (listening indicators). Never thread truth. */
const DictationSpeechStartedEvent = Schema.Struct({
  type: Schema.Literal("speech_started"),
});

const DictationSpeechStoppedEvent = Schema.Struct({
  type: Schema.Literal("speech_stopped"),
});

/** Clean end of the dictation; the stream completes after this event. */
const DictationStoppedEvent = Schema.Struct({
  type: Schema.Literal("stopped"),
});

export const DictationStreamEvent = Schema.Union([
  DictationStartedEvent,
  DictationTranscriptEvent,
  DictationSpeechStartedEvent,
  DictationSpeechStoppedEvent,
  DictationStoppedEvent,
  Schema.Struct({
    type: Schema.Literal("utterance_completed"),
    ...CompletedDictationUtterance.fields,
  }),
]);
export type DictationStreamEvent = typeof DictationStreamEvent.Type;

export class DictationNotFoundError extends Schema.TaggedErrorClass<DictationNotFoundError>()(
  "DictationNotFoundError",
  {
    dictationId: Schema.String,
  },
) {
  override get message() {
    return `Unknown dictation: ${this.dictationId}`;
  }
}

/** The speech worker failed this dictation (spawn, model, or decode failure). */
export class DictationWorkerError extends Schema.TaggedErrorClass<DictationWorkerError>()(
  "DictationWorkerError",
  {
    detail: Schema.String,
  },
) {
  override get message() {
    return `Dictation speech worker failed: ${this.detail}`;
  }
}

export const DictationError = Schema.Union([DictationNotFoundError, DictationWorkerError]);
export type DictationError = typeof DictationError.Type;

const DictationRpcError = Schema.Union([DictationError, EnvironmentAuthorizationError]);

export const WsDictationStartRpc = Rpc.make(DICTATION_WS_METHODS.dictationStart, {
  payload: DictationStartInput,
  success: DictationStreamEvent,
  error: DictationRpcError,
  stream: true,
});

export const WsDictationAppendRpc = Rpc.make(DICTATION_WS_METHODS.dictationAppend, {
  payload: DictationAppendInput,
  error: DictationRpcError,
});

export const WsDictationFlushRpc = Rpc.make(DICTATION_WS_METHODS.dictationFlush, {
  payload: DictationFlushInput,
  error: DictationRpcError,
});

export const WsDictationStatusRpc = Rpc.make(DICTATION_WS_METHODS.dictationStatus, {
  payload: DictationSessionInput,
  success: DictationStatus,
  error: DictationRpcError,
});

export const WsDictationStopRpc = Rpc.make(DICTATION_WS_METHODS.dictationStop, {
  payload: DictationStopInput,
  error: DictationRpcError,
});

/**
 * The dictation contract as its own group, merged into the served `WsRpcGroup`
 * in rpc.ts. The server side lives in `apps/server/src/speech`: the dictation
 * manager buffers PCM, cuts segments, and decodes them in the speech worker
 * child process. Keeping the group separate lets the acceptance rig serve
 * exactly this surface without the rest of the server graph.
 */
export const DictationRpcGroup = RpcGroup.make(
  WsDictationStartRpc,
  WsDictationAppendRpc,
  WsDictationFlushRpc,
  WsDictationStopRpc,
  WsDictationStatusRpc,
);
